/**
 * Canonical WhatsApp link builder.
 *
 * Centralizes the phone number + pre-filled-message logic so every CTA
 * across the site points at the same number and the "Olá, estou
 * interessado no <produto>" template doesn't drift between components.
 *
 * Keep the phone number here — the other modules import from this file.
 */

/** WhatsApp uses E.164 without the `+`. Brazil country code 55. */
const PHONE = "5511972273199";

// `WHATSAPP_DISPLAY` (the +55 (11) 97227-3199 plain-text version) was
// dropped here — knip showed zero callers anywhere in src/. Footer
// and Contact page render the number directly. If a "phone-display"
// component reappears later, restore from git history.

/** URL-encode helper that doesn't turn spaces into `+` (WhatsApp prefers %20). */
function encode(text: string): string {
  return encodeURIComponent(text);
}

/**
 * Build a wa.me URL, optionally with a pre-filled chat message.
 *
 * @param text  Optional message to pre-fill. Kept concise (WhatsApp web
 *              renders long messages awkwardly).
 */
export function whatsappUrl(text?: string): string {
  const base = `https://wa.me/${PHONE}`;
  if (!text) return base;
  return `${base}?text=${encode(text)}`;
}

/**
/**
 * Convenience combo: URL for a product. Generates a "Olá, tenho
 * interesse no <product>. Gostaria de saber mais." opener and wraps
 * it in the WhatsApp click-to-chat URL.
 *
 * The previous public `productInterestMessage` helper was inlined
 * here in PR #28 — only this combo function is consumed externally,
 * and exposing the message as a separate export drew knip + made
 * editors think they had two ways to share a product when really the
 * URL form is what every caller wants.
 */
export function whatsappProductUrl(productName: string): string {
  const message = `Olá, tenho interesse no ${productName}. Gostaria de saber mais.`;
  return whatsappUrl(message);
}

/** Support / assistance-technique shortcut kept here so the text is reviewable in one place. */
export function whatsappSupportUrl(message?: string): string {
  return whatsappUrl(message ?? "Olá, preciso de assistência técnica.");
}

/** Generic "fale conosco" opener — home/header/footer CTAs. */
export function whatsappContactUrl(): string {
  return whatsappUrl("Olá, gostaria de falar com a equipe Or Brasil.");
}
